import { useState, useEffect } from 'react';
import { ConciergeBell, MessageCircle, CheckCircle, Lightbulb, Bug, Star, TrendingUp, Sparkles } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import { supabase } from '@services/api.js';
import { usePageTitle } from '@hooks/usePageTitle';
import ContourBG from '@components/ContourBG';

const FEEDBACK_TYPES = [
  { id: 'bug',     label: 'Bug report',      icon: Bug },
  { id: 'feature', label: 'Feature request', icon: Lightbulb },
  { id: 'general', label: 'General',         icon: Star },
];

const TYPE_ROW_HEIGHT = 48;
const TEXTAREA_ROWS   = 5;

/* Community Pulse only shows a skill once it clears this many votes.
   This is unmoderated free text (the "request a skill" form below), not
   a curated list — the floor is what keeps junk like "sfdfasdf" or "6yy"
   out of the product. The 8 early-stage ideas have their own leaderboard
   now (Skills page) with no floor at all, because that list IS curated;
   this panel's job is specifically to surface demand for something
   nobody's made a candidate for yet, so it stays deliberately harder to
   clear. */
const PULSE_VOTE_FLOOR = 5;

/* Known skill ids → display names, so a vote stored as "precision-storytelling"
   shows as "Precision Storytelling". Anything not listed just gets title-cased. */
const KNOWN_SKILL_NAMES = {
  'ai-prompting': 'AI Prompt Engineering',
  'sales-negotiation': 'Sales & Negotiation',
  copywriting: 'Copywriting',
  'precision-storytelling': 'Precision Storytelling',
  'technical-translation': 'Technical Translation',
};

/* Excluded from Community Pulse entirely, not just floored — these are
   the 5 real skills (requesting a thing that already exists is noise
   here) and the 8 skill_candidates ideas (they have their own
   leaderboard on the Skills page now; showing them twice, uncoordinated,
   would just be confusing about which vote count is the real one). */
const EXCLUDED_SKILL_NAMES = new Set([
  ...Object.keys(KNOWN_SKILL_NAMES),
  'family-table', 'hard-conversations', 'pitch-craft', 'deep-listening',
  'socratic-questioning', 'coaching-mentorship', 'perception-editing', 'learning-edge',
]);

const prettifySkillName = (raw) => {
  if (!raw) return null;
  if (KNOWN_SKILL_NAMES[raw]) return KNOWN_SKILL_NAMES[raw];
  return raw.replace(/[-_]/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
};

const Feedback = () => {
  usePageTitle('Feedback & Requests — Castle Atlas');
  const { user, profile } = useAuth();

  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const [activeTab, setActiveTab] = useState('feedback');
  const [fbType, setFbType]       = useState('');
  const [fbText, setFbText]       = useState('');
  const [fbLoading, setFbLoading] = useState(false);
  const [fbDone, setFbDone]       = useState(false);
  const [skillName, setSkillName]       = useState('');
  const [skillWhy, setSkillWhy]         = useState('');
  const [skillLoading, setSkillLoading] = useState(false);
  const [skillDone, setSkillDone]       = useState(false);

  /* ── Community Pulse — real data from skill_requests ── */
  const [pulse, setPulse] = useState({ loading: true, skills: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // get_skill_vote_counts() is the security-definer function from
        // migration 008 — it returns real cross-user totals. A plain
        // select on skill_requests would hit its owner-scoped RLS and
        // only ever return the viewer's own rows (0 or 1 per skill).
        const { data, error } = await supabase.rpc('get_skill_vote_counts');
        if (error) throw error;
        const ranked = (data || [])
          .map((row) => ({ key: (row.skill_name || '').trim().toLowerCase(), votes: Number(row.votes) }))
          .filter((row) => row.key && !EXCLUDED_SKILL_NAMES.has(row.key))
          .filter((row) => row.votes >= PULSE_VOTE_FLOOR)
          .map((row) => ({ name: prettifySkillName(row.key), votes: row.votes }))
          .sort((a, b) => b.votes - a.votes)
          .slice(0, 5);
        if (!cancelled) setPulse({ loading: false, skills: ranked });
      } catch {
        if (!cancelled) setPulse({ loading: false, skills: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleFeedback = async (e) => {
    e.preventDefault();
    if (!fbType || !fbText.trim()) return;
    setFbLoading(true);
    try {
      await supabase.from('feedback').insert({ user_id: user?.id, email: user?.email, name: profile?.full_name, type: fbType, message: fbText.trim(), source: 'feedback_page' });
      setFbDone(true); setFbText(''); setFbType('');
    } catch (err) { console.error(err); }
    finally { setFbLoading(false); }
  };

  const handleSkillRequest = async (e) => {
    e.preventDefault();
    if (!skillName.trim()) return;
    setSkillLoading(true);
    try {
      await supabase.from('skill_requests').insert({ user_id: user?.id, email: user?.email, skill_name: skillName.trim(), reason: skillWhy.trim() });
      setSkillDone(true); setSkillName(''); setSkillWhy('');
    } catch (err) { console.error(err); }
    finally { setSkillLoading(false); }
  };

  const headColor = isDark ? '#fafaf9' : '#1c1917';
  const mutedColor = isDark ? '#78716c' : '#a8a29e';
  const dashColor = isDark ? '#57534e' : '#d4cdc7';
  const pageBg = isDark ? '#0e0d0c' : '#faf9f7';
  const showPulse = !pulse.loading && pulse.skills.length > 0;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: pageBg }}>
      <style>{`
        @keyframes fade-up{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        .fu{animation:fade-up 0.3s ease-out both}
        .fu2{animation:fade-up 0.3s ease-out 0.06s both}
        .fu3{animation:fade-up 0.3s ease-out 0.12s both}
        .orange-glow{transition:all 0.2s ease}
        .orange-glow.active{background:#ea580c!important;color:white!important;box-shadow:0 4px 12px rgba(234,88,12,0.3)}
        .orange-glow.active svg{color:white!important}
        .fb-layout{display:flex;gap:24px;align-items:flex-start}
        @media(max-width:900px){.fb-layout{flex-direction:column}}
        @media(prefers-reduced-motion:reduce){.fu,.fu2,.fu3{animation:none!important}}
      `}</style>

      {/* ── Header — Atlas Plates ── */}
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 150, overflow: 'hidden', pointerEvents: 'none' }}>
          <ContourBG isDark={isDark} size="compact" opacity={0.9} />
        </div>
        <div className="fu rail-clearance" style={{ maxWidth: '100%', padding: '48px 24px 20px', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: '#ea580c', letterSpacing: '0.14em' }}>YOUR VOICE</span>
            <span style={{ width: 22, height: 1, background: dashColor, display: 'inline-block' }} />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: mutedColor, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Feedback</span>
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.7rem,4.5vw,2.2rem)', fontWeight: 900, letterSpacing: '-0.03em', color: headColor, margin: '0 0 8px', lineHeight: 1.1 }}>
            Feedback &amp; Requests
          </h1>
          <p style={{ fontSize: 14, color: mutedColor, margin: 0, maxWidth: 460, lineHeight: 1.6 }}>
            Tell us what's broken, what you love, or what skills you want next. We read everything.
          </p>
        </div>
      </div>

      <div className="rail-clearance" style={{ maxWidth: '100%', padding: '20px 24px 72px' }}>
        <div className="fb-layout">
          {/* LEFT */}
          <div style={{ flex: 3, width: '100%' }} className="fu2">
            <div className="flex gap-2 mb-6 p-1 bg-stone-100 dark:bg-stone-800 rounded-xl w-fit">
              {[
                { id: 'feedback', label: 'Send feedback',   icon: MessageCircle },
                { id: 'skill',    label: 'Request a skill', icon: ConciergeBell },
              ].map(tab => (
                <button key={tab.id} onClick={() => { setActiveTab(tab.id); setFbDone(false); setSkillDone(false); }}
                  className={`orange-glow flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === tab.id ? 'active' : 'text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300'}`}>
                  <tab.icon size={15} />{tab.label}
                </button>
              ))}
            </div>

            <div className="card fu3">
              {activeTab === 'feedback' && (
                fbDone ? (
                  <div className="flex flex-col items-center py-12 text-center">
                    <CheckCircle size={48} className="text-green-500 mb-4" />
                    <p className="text-base font-semibold text-stone-900 dark:text-stone-100 mb-2">Thanks for the feedback!</p>
                    <p className="text-sm text-stone-500 mb-6">We read everything. If it's a bug, you will hear back within 48 hours.</p>
                    <button onClick={() => setFbDone(false)} className="btn-ghost text-sm">Send another message</button>
                  </div>
                ) : (
                  <form onSubmit={handleFeedback} className="space-y-5">
                    <div>
                      <label className="section-label block mb-2.5">Type</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, minHeight: TYPE_ROW_HEIGHT }}>
                        {FEEDBACK_TYPES.map(t => (
                          <button key={t.id} type="button" onClick={() => setFbType(t.id)}
                            style={{ height: TYPE_ROW_HEIGHT, flex: '1 1 120px' }}
                            className={`flex items-center justify-center gap-2 px-4 rounded-xl border-2 transition-all whitespace-nowrap ${fbType === t.id ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' : 'border-stone-200 dark:border-stone-700 hover:border-stone-300'}`}>
                            <t.icon size={20} className={fbType === t.id ? 'text-orange-500 shrink-0' : 'text-stone-400 shrink-0'} />
                            <span className={`text-sm font-semibold ${fbType === t.id ? 'text-orange-600 dark:text-orange-400' : 'text-stone-600 dark:text-stone-400'}`}>{t.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="section-label block mb-2">Message</label>
                      <textarea value={fbText} onChange={e => setFbText(e.target.value)}
                        placeholder="Example: 'The quiz on Module 3 doesn't save my progress after refreshing...'"
                        rows={TEXTAREA_ROWS} className="input resize-none w-full" />
                    </div>
                    <button type="submit" disabled={!fbType || !fbText.trim() || fbLoading}
                      className="w-full py-3 rounded-xl font-bold text-sm text-white border-none cursor-pointer transition-all bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:bg-stone-400">
                      {fbLoading ? 'Sending...' : 'Send feedback'}
                    </button>
                  </form>
                )
              )}

              {activeTab === 'skill' && (
                skillDone ? (
                  <div className="flex flex-col items-center py-12 text-center">
                    <CheckCircle size={48} className="text-green-500 mb-4" />
                    <p className="text-base font-semibold text-stone-900 dark:text-stone-100 mb-2">Request received!</p>
                    <p className="text-sm text-stone-500 mb-6">We prioritize skills based on demand. The more votes, the faster we build.</p>
                    <button onClick={() => setSkillDone(false)} className="btn-ghost text-sm">Request another skill</button>
                  </div>
                ) : (
                  <form onSubmit={handleSkillRequest} className="space-y-5">
                    <div>
                      <label className="section-label block mb-2.5">What skill do you want?</label>
                      <input type="text" value={skillName} onChange={e => setSkillName(e.target.value)}
                        placeholder="e.g. Public speaking, SQL for data analysis, Negotiation"
                        className="input w-full" style={{ height: TYPE_ROW_HEIGHT, boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label className="section-label block mb-2">
                        Why do you want this?{' '}
                        <span className="text-stone-300 dark:text-stone-600 normal-case font-normal">(optional)</span>
                      </label>
                      <textarea value={skillWhy} onChange={e => setSkillWhy(e.target.value)}
                        placeholder="How would this skill help you in your work or learning journey?"
                        rows={TEXTAREA_ROWS} className="input resize-none w-full" />
                    </div>
                    <button type="submit" disabled={!skillName.trim() || skillLoading}
                      className="w-full py-3 rounded-xl font-bold text-sm text-white border-none cursor-pointer transition-all bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:bg-stone-400">
                      {skillLoading ? 'Submitting...' : 'Request this skill'}
                    </button>
                  </form>
                )
              )}
            </div>
          </div>

          {/* RIGHT — Community Pulse (only rendered when real data clears the floor) */}
          {showPulse && (
            <div style={{ flex: 1, minWidth: 260, width: '100%' }} className="fu3">
              <div className="card">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp size={18} className="text-orange-500" />
                  <h3 style={{ fontFamily: 'var(--font-mono)' }} className="text-xs font-bold uppercase tracking-wider text-stone-500">Community Pulse</h3>
                </div>
                <div>
                  <div className="text-xs font-semibold text-stone-400 mb-3 flex items-center gap-1">
                    <ConciergeBell size={12} /> Most requested skills
                  </div>
                  <div className="space-y-3">
                    {pulse.skills.map((skill, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <span className="text-stone-700 dark:text-stone-300">{skill.name}</span>
                        <span className="text-xs font-mono text-orange-600 bg-orange-50 dark:bg-orange-900/20 px-2 py-0.5 rounded-full">{skill.votes} votes</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="pt-6 mt-4 text-center text-xs text-stone-400 border-t border-stone-100 dark:border-stone-800">
                  Every submission is read by our team within 48 hours.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Trust line — real, non-fabricated */}
        <div className="fu3" style={{ marginTop: 28, textAlign: 'center' }}>
          <span className="inline-flex items-center gap-1.5 bg-stone-100 dark:bg-stone-800 px-3.5 py-1.5 rounded-full text-sm text-stone-600 dark:text-stone-400">
            <Sparkles size={14} className="text-orange-500" />
            Every message is read by a human within 48 hours
          </span>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
